import {
  getTaxFullUrl,
  getVerificationBypass,
  isSyntheticMonitor,
  sanitizeLooseTextInput,
  sanitizeTightTextInput
} from '../../../shared/utils';
import { ReferralResponse } from '../../../modules/ReferralForm/api/createReferral';
import { getAnsweredString, MatcherAnswers } from '../flow/types';
import { getNameParts } from '../flow/getNameParts';
import { mapServicesToInterests } from '../flow/mapServicesToInterests';

interface MatchingReferralInput {
  matcherAnswers: MatcherAnswers;
  email: string | null;
  partnershipUrn: string;
  matchResultKey: string;
  recaptchaToken: string;
  proAgreement: string;
  ramseyAgreement: string;
  verificationToken?: string;
  leadSource?: string;
}

interface MatchingReferralRequestBody {
  firstName: string;
  lastName: string;
  phone: string;
  postalCode: string;
  email: string | null;
  partnershipUrn: string;
  matchResultKey: string;
  interests: string[];
  leadSource: string;
  agreement: string;
  proAgreement: string;
  answers: MatcherAnswers;
  recaptcha: { override?: string; response?: string };
  verificationToken?: string;
  syntheticMonitor: boolean;
}

function getLeadSource(): string {
  return (
    new URLSearchParams(globalThis.location.search).get('lead_source') ?? ''
  );
}

function createRecaptcha(responseToken: string) {
  const bypassVerificationQueryParam = getVerificationBypass();
  if (bypassVerificationQueryParam) {
    return { override: bypassVerificationQueryParam };
  }
  return { response: responseToken };
}

function buildMatchingReferralRequest({
  matcherAnswers,
  email,
  partnershipUrn,
  matchResultKey,
  recaptchaToken,
  proAgreement,
  ramseyAgreement,
  verificationToken,
  leadSource = getLeadSource()
}: MatchingReferralInput): MatchingReferralRequestBody {
  const [firstName, lastName] = getNameParts(matcherAnswers);
  return {
    firstName: sanitizeTightTextInput(firstName),
    lastName: sanitizeTightTextInput(lastName),
    phone: getAnsweredString(matcherAnswers.phone).replace(/\D/g, ''),
    postalCode: getAnsweredString(matcherAnswers.postalCode),
    email: email || null,
    partnershipUrn,
    matchResultKey,
    interests: mapServicesToInterests(matcherAnswers),
    leadSource,
    agreement: sanitizeLooseTextInput(ramseyAgreement),
    proAgreement: sanitizeLooseTextInput(proAgreement),
    answers: matcherAnswers,
    recaptcha: createRecaptcha(recaptchaToken),
    // Omitted rather than sent empty when absent (the bypass path), so the backend's optional-field
    // validation sees no key at all instead of a value it has to treat as missing.
    ...(verificationToken ? { verificationToken } : {}),
    syntheticMonitor: isSyntheticMonitor()
  };
}

async function createMatchingReferral(
  input: MatchingReferralInput,
  baseURL = getTaxFullUrl()
) {
  const response = await fetch(`${baseURL}/api/matching/referral`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(buildMatchingReferralRequest(input))
  });
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  return (await response.json()) as ReferralResponse;
}

export { createMatchingReferral, buildMatchingReferralRequest };
